import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ArrowLeft, Briefcase, Trash2 } from 'lucide-react';
import { getActivityTypeColor } from '../utils/activityColor';

const DATE_SHORTCUTS = [
  { id: 'all', label: 'All time', days: null },
  { id: 'today', label: 'Today', days: 0 },
  { id: 'week', label: 'Last week', days: 7 },
  { id: '3w', label: 'Last 3 weeks', days: 21 },
  { id: '6m', label: 'Last 6 months', days: 183 },
  { id: 'year', label: 'Last year', days: 365 },
];

const ActivityFilterPanel = ({
  activities = [],
  hiddenIds = [],
  onToggle,
  onToggleType,
  commutes,
  onCommutesChange,
  allDates,
  onAllDatesChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  activitySettings,
  onDeleteActivity,
  dateRightSlot,
}) => {
  const [expandedType, setExpandedType] = useState(null);
  const [activeShortcut, setActiveShortcut] = useState(allDates ? 'all' : 'custom');
  // Date shortcut row is collapsed by default; tapping the current selection
  // expands the full set of options, and selecting one collapses it again.
  const [dateRangeOpen, setDateRangeOpen] = useState(false);

  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);

  const groupedSorted = useMemo(() => {
    const groups = activities.reduce((acc, activity) => {
      const type = activity.type || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(activity);
      return acc;
    }, {});
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [activities]);

  const typeHiddenState = (typeActivities) => {
    const hiddenCount = typeActivities.filter(a => hiddenSet.has(a.id)).length;
    if (hiddenCount === 0) return 'all';
    if (hiddenCount === typeActivities.length) return 'none';
    return 'some';
  };

  const handleTypeToggleAll = (type, typeActivities) => {
    const state = typeHiddenState(typeActivities);
    onToggleType?.(type, state === 'all');
  };

  const applyShortcut = (shortcut) => {
    setActiveShortcut(shortcut.id);
    if (shortcut.id === 'all') {
      onAllDatesChange?.(true);
      setDateRangeOpen(false);
      return;
    }
    if (shortcut.id === 'custom') {
      onAllDatesChange?.(false);
      // Keep the panel open so the user can interact with the date pickers.
      setDateRangeOpen(true);
      return;
    }
    onAllDatesChange?.(false);
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - shortcut.days);
    onFromDateChange?.(start);
    onToDateChange?.(end);
    setDateRangeOpen(false);
  };

  const showDateControls = !!onAllDatesChange;

  const currentShortcutLabel = (() => {
    if (activeShortcut === 'custom') return 'Custom';
    const match = DATE_SHORTCUTS.find(s => s.id === activeShortcut);
    return match ? match.label : 'All time';
  })();

  const expandedTypeActivities = expandedType
    ? groupedSorted.find(([t]) => t === expandedType)?.[1] || []
    : [];

  return (
    <div className="border rounded bg-white">
      <div className="p-3 border-b space-y-3 bg-gray-50">
        {showDateControls && (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-700">Pick from:</span>

                {!dateRangeOpen && (
                  <button
                    type="button"
                    onClick={() => setDateRangeOpen(true)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${activeShortcut === 'custom'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-gray-900 text-white border-gray-900'
                      }`}
                  >
                    {currentShortcutLabel}
                  </button>
                )}

                {dateRangeOpen && (
                  <>
                    {DATE_SHORTCUTS.map(s => {
                      const isActive = activeShortcut === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => applyShortcut(s)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${isActive
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white border-gray-300 hover:bg-gray-100'
                            }`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => applyShortcut({ id: 'custom', days: 'custom' })}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${activeShortcut === 'custom'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white border-blue-500 text-blue-600 hover:bg-blue-50'
                        }`}
                    >
                      Custom
                    </button>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 ml-auto">
                {onCommutesChange && (
                  <button
                    type="button"
                    onClick={() => onCommutesChange(!commutes)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors ${commutes
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white border-gray-300 hover:bg-gray-100'
                      }`}
                    title={commutes ? 'Commutes included' : 'Commutes excluded'}
                  >
                    <Briefcase size={16} />
                    <span>Commutes {commutes ? '✓' : '×'}</span>
                  </button>
                )}
                {dateRightSlot}
              </div>
            </div>

            {dateRangeOpen && activeShortcut === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col text-sm">
                  <label className="mb-1 text-gray-600">From</label>
                  <DatePicker
                    selected={fromDate}
                    onChange={onFromDateChange}
                    className="w-full px-2 py-1.5 border rounded text-sm"
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={30}
                  />
                </div>
                <div className="flex flex-col text-sm">
                  <label className="mb-1 text-gray-600">To</label>
                  <DatePicker
                    selected={toDate}
                    onChange={onToDateChange}
                    className="w-full px-2 py-1.5 border rounded text-sm"
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={30}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
        {!expandedType && (
          <div className="text-sm font-medium text-gray-700">
            {activities.length === 0
              ? 'No activities in current view'
              : `${activities.length} activities in view`}
          </div>
        )}

        {activities.length === 0 && (
          <p className="text-xs text-gray-500">
            Pan or zoom the map to find activities.
          </p>
        )}

        {/* Overview: chip row of types. Hidden while drilling into a type. */}
        {!expandedType && groupedSorted.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {groupedSorted.map(([type, typeActivities]) => {
              const state = typeHiddenState(typeActivities);
              const color = getActivityTypeColor(type, activitySettings);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setExpandedType(type)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full border border-gray-300 text-sm transition-colors bg-white hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={state === 'all'}
                    ref={el => { if (el) el.indeterminate = state === 'some'; }}
                    onChange={(e) => { e.stopPropagation(); }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeToggleAll(type, typeActivities);
                    }}
                    className="h-5 w-5 cursor-pointer"
                    style={{ accentColor: color }}
                  />
                  <span className="font-medium">{type}</span>
                  <span className="text-sm text-gray-500">{typeActivities.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Drilled-in view: back button + activities for the expanded type. */}
        {expandedType && (() => {
          const expandedColor = getActivityTypeColor(expandedType, activitySettings);
          return (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpandedType(null)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border border-gray-300 bg-white hover:bg-gray-100"
                >
                  <ArrowLeft size={16} />
                  <span>Back</span>
                </button>
                <span className="font-semibold text-sm">{expandedType}</span>
                <span className="text-xs text-gray-500">
                  ({expandedTypeActivities.length})
                </span>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {expandedTypeActivities.map(activity => (
                  <div
                    key={activity.id}
                    className="flex items-center gap-2 text-sm min-w-0 py-1"
                  >
                    <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!hiddenSet.has(activity.id)}
                        onChange={() => onToggle?.(activity.id)}
                        className="h-5 w-5 flex-shrink-0"
                        style={{ accentColor: expandedColor }}
                      />
                      <span className="truncate flex-1" title={activity.name}>
                        {activity.name}
                      </span>
                    </label>
                    {typeof activity.distance === 'number' && (
                      <span className="text-gray-500 flex-shrink-0 text-xs">
                        {(activity.distance / 1000).toFixed(1)} km
                      </span>
                    )}
                    {onDeleteActivity && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete "${activity.name}"?`)) {
                            onDeleteActivity(activity.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                        title="Delete activity"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ActivityFilterPanel;
